// Phase 4c — `(table …)` tile op.
//
// `(table :span [W H] (column …)+)`
//
// Positional children dispatch through `TABLE_CHILD_TABLE`
// (context-restricted to `column` only); at least one is required.
// Registered in `dashboardMeta` (NOT `opMeta`).

import { z } from 'zod';

import type { AstInput } from '../../../normalize.ts';
import { paramPath } from '../../../path.ts';
import { spanArg } from '../args.ts';
import { type DashboardCtx, dashboardMeta, dispatchDashboardChild } from '../dispatch.ts';
import { TABLE_CHILD_TABLE, tableChildScope } from '../sub-forms.ts';
import type { ColumnSpec, TableSpec } from '../value.ts';

export const table = z
  .object({
    name: z.literal('table'),
    params: z.array(z.unknown()).min(1),
    span: spanArg,
  })
  .describe('A tabular tile. Positional children: `(column …)` (≥1), each an explicit `(col ds colname)` ref.')
  .register(dashboardMeta, {
    evaluation: 'special',
    effect: 'pure',
    childScopes: [tableChildScope],
    example: { expr: '(table (column :ref (col wells region) :label "Region" :format text) :span [12 4])' },
    apply: async (data, ctx: DashboardCtx): Promise<TableSpec> => {
      const children = await Promise.all(
        data.params.map((node, i) =>
          dispatchDashboardChild(node as AstInput, TABLE_CHILD_TABLE, ctx, paramPath(ctx.path, i)),
        ),
      );
      // `TABLE_CHILD_TABLE` only resolves `column`, so every child is a
      // ColumnSpec; the cast is exhaustive by construction.
      return { kind: 'table-spec', span: data.span, columns: children as readonly ColumnSpec[] };
    },
  });
